<template>
  <div
      v-if="visible"
      :style="menuStyle"
      class="layer-context-menu"
      role="menu"
      aria-label="Layer actions"
      @click.stop
      @contextmenu.prevent
  >
    <template v-for="item in items">
      <div
          v-if="item.divider"
          :key="item.id"
          class="menu-divider"
          role="separator"
      />
      <button
          v-else
          :key="item.id"
          type="button"
          role="menuitem"
          :disabled="item.disabled"
          :aria-disabled="item.disabled ? 'true' : 'false'"
          :class="{ 'menu-item--disabled': item.disabled, 'menu-item--open': submenu && submenu.item === item }"
          class="menu-item"
          @mouseenter="openSubmenu(item, $event)"
          @click="onItemClick(item, $event)"
      >
        <layer-icon
            v-if="item.icon"
            :name="item.icon"
            :size="15"
        />

        <span class="menu-label">{{ item.label }}</span>
        <kbd
            v-if="item.shortcut"
            class="menu-shortcut"
        >{{ item.shortcut }}</kbd>
        <layer-icon
            v-if="item.children"
            name="chevron-right"
            :size="13"
            class="menu-submenu-arrow"
        />
      </button>
    </template>

    <!-- A submenu is the same menu, opened next to its parent item. -->
    <layer-context-menu
        v-if="submenu"
        ref="submenu"
        :visible="true"
        :x="submenu.x"
        :y="submenu.y"
        :items="submenu.item.children"
        @select="$emit('select', $event)"
    />
  </div>
</template>

<script>
import LayerIcon from '@/components/Layer/LayerIcon.vue';

export default {
  name:       'LayerContextMenu',
  components: { LayerIcon },
  props: {
    visible: { type: Boolean, default: false },
    x:       { type: Number, default: 0 },
    y:       { type: Number, default: 0 },
    items:   { type: Array, default: () => [] },
  },
  data() {
    return {
      submenu: null,
    };
  },
  computed: {
    menuStyle() {
      return {
        left: `${this.x}px`,
        top:  `${this.y}px`,
      };
    },
  },
  watch: {
    visible() {
      this.submenu = null;
    },
  },
  methods: {
    onItemClick(item, event) {
      if (item.disabled) { return; }
      if (item.children) {
        this.openSubmenu(item, event);
        return;
      }
      this.$emit('select', item.id);
    },
    // Opens the item's submenu to the right, or to the left when there is no room. Hovering any other item closes it.
    openSubmenu(item, event) {
      if (!item.children || item.disabled) {
        this.submenu = null;
        return;
      }
      const itemRect = event.currentTarget.getBoundingClientRect();
      this.submenu   = { item, x: itemRect.right, y: itemRect.top - 4 };

      this.$nextTick(() => {
        const rect = this.$refs.submenu.$el.getBoundingClientRect();
        if (rect.right > window.innerWidth) {
          this.submenu.x = itemRect.left - rect.width;
        }
        if (rect.bottom > window.innerHeight) {
          this.submenu.y -= rect.bottom - window.innerHeight;
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.layer-context-menu {
  position: fixed;
  z-index: 10000;
  box-sizing: border-box;
  width: max-content;
  min-width: 212px;
  padding: 5px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0 0 0 1px #E7E9ED, 0 12px 32px rgba(16, 24, 40, 0.16), 0 2px 6px rgba(16, 24, 40, 0.06);
  font-family: 'Geist', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif;
  color: #14161B;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 32px;
  width: 100%;
  padding: 0 10px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #14161B;
  cursor: pointer;
  font-family: inherit;
  text-align: left;

  &:focus {
    outline: none;
    border: 0;
  }

  &:not(.menu-item--disabled):hover,
  &--open {
    background: #F3F4F6;
    color: #14161B;
  }

  &--disabled,
  &:disabled {
    color: #9AA0AA;
    opacity: 0.55;
    cursor: not-allowed;
  }
}

.menu-label {
  flex-grow: 1;
  font-size: 12.5px;
}

.menu-shortcut {
  margin-left: auto;
  padding: 2px 6px;
  border-radius: 4px;
  background: #F3F4F6;
  color: #5E6573;
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
}

.menu-submenu-arrow {
  margin-left: auto;
  color: #5E6573;
}

.menu-divider {
  height: 1px;
  margin: 4px 6px;
  background: #E7E9ED;
}
</style>
